import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";

import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { selectPanelClosingRows } from "../utils/followedClosingSoon";
import {
  canUseAdminSeedPreview,
  fetchLatestDropForCreator,
  loadAdminSeedPreview,
  normalizeCreator,
} from "../utils/homeSeedPreview";

/**
 * Latest drop per followed creator. The games panel keeps Home's <=15 rule
 * first, then fills remaining slots with other open drops from people you
 * follow. The Home sidebar does not use this list.
 */
export default function useFollowedClosingSoon() {
  const { currentUser, userProfile } = useAuth();
  const [rows, setRows] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setReady(false);
      if (!currentUser) {
        if (!cancelled) {
          setRows([]);
          setReady(true);
        }
        return;
      }

      const userDoc = await getDoc(doc(db, "users", currentUser.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};
      let followingIds = userData.following || [];

      if (canUseAdminSeedPreview(userProfile) && followingIds.length === 0) {
        const preview = await loadAdminSeedPreview();
        followingIds = preview.following || [];
      }

      const loaded = await Promise.all(
        followingIds.map(async (creatorId) => {
          const creatorDoc = await getDoc(doc(db, "users", creatorId));
          if (!creatorDoc.exists()) return null;
          const creator = normalizeCreator(creatorId, creatorDoc.data());
          const drop = await fetchLatestDropForCreator(creatorId);
          return { creator, drop };
        })
      );

      if (!cancelled) {
        setRows(selectPanelClosingRows(loaded.filter(Boolean)));
        setReady(true);
      }
    }

    load().catch(() => {
      if (!cancelled) {
        setRows([]);
        setReady(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [currentUser, userProfile]);

  return { rows, ready };
}
